import {
    DOCUMENT_TITLE_CONTEXT,
    useDocumentTitle,
} from '@thoughtspot/blink-context';
import { cleanHTMLTags } from '@thoughtspot/browser-util';
import { envFlags, FLAGS } from '@thoughtspot/env-flags';
import { useTranslation } from '@thoughtspot/i18n';
import { create } from '@thoughtspot/logger';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import { clearQueue } from '@thoughtspot/viz-render-queue';
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useUpdateEffect } from 'react-use';
import { useAppContext } from '/@contexts/global-app-context';
import { useLoadAnswerSessionMutation } from '/@services/answer/answer-service';
import {
    AnswerContextType,
    SageSearchResponse,
} from '/@services/generated/graphql-types';
import { getProductName } from '/@services/system/config-service/session-service';
import { isEmbeddedApp } from '/@utils/embed-base.util';
import { useApolloClient } from '/@utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import { useRuntimeParams } from '/@utils/runtime-filters.util';
import {
    getAccessTypeForObject,
    getCommonAnswerPageProperties,
} from '/@utils/viz/metrics-event.util';
import {
    clearSavedFromSpreadsheetFlag,
    readCachedAnswerClientState,
    shouldReopenInSpreadsheet,
    wasSavedFromSpreadsheet,
} from '../components/answer-spreadsheet/saved-from-spreadsheet';
import { NavigationModalContainerProps } from '../components/navigation-blocker/navigation-blocker-modal.container';
import { BaseAnswerLayoutContextValue } from './base-answer-layout-context';

const logger = create('saved-answer-layout');

export interface SavedAnswerLayoutContextValue
    extends BaseAnswerLayoutContextValue {
    navModalProps: NavigationModalContainerProps;
}

const SavedAnswerLayoutContext = createContext<SavedAnswerLayoutContextValue | null>(
    null,
);
SavedAnswerLayoutContext.displayName = 'SavedAnswerLayoutContext';

const SavedAnswerLayoutProviderInner: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const { answerId = '' } = useParams<{ answerId: string }>();
    const { t } = useTranslation();
    const runtimeParams = useRuntimeParams();
    const { metricsService } = useAppContext();
    const companyTitle = getProductName();

    const [answerSessionId, setAnswerSessionId] = useState('');
    const [answerPageProperties, setAnswerPageProperties] = useState(null);

    // Persisted across the Answer<->Spreadsheet child-route remount (this
    // provider stays mounted). Lets the Answer view restore sage tokens and
    // skip auto-focus when returning from the Spreadsheet.
    const [
        cachedSageQueryState,
        setCachedSageQueryState,
    ] = useState<SageSearchResponse | null>(null);
    const [hasVisitedSpreadsheet, setHasVisitedSpreadsheet] = useState(false);
    const markSpreadsheetVisited = useCallback(
        () => setHasVisitedSpreadsheet(true),
        [],
    );

    const { setDocumentTitle } = useDocumentTitle({
        companyTitle,
    } as DOCUMENT_TITLE_CONTEXT);

    const onLoadSessionComplete = (
        sessionId: string,
        pinboardName?: string,
    ) => {
        setAnswerSessionId(sessionId);
        setDocumentTitle?.(cleanHTMLTags(pinboardName));
    };

    const { loadAnswer, data, loading, error } = useLoadAnswerSessionMutation(
        answerId,
        onLoadSessionComplete,
        false,
        {
            isClientPaginated: true,
            size: envFlags.getValue(FLAGS.dataBatchSize?.name) || 1000,
            offset: 0,
        },
        AnswerContextType.SavedAnswer,
        ['*'],
    );

    const navModalProps: NavigationModalContainerProps = {
        title: t('answer.unsavedChangesAlert.title'),
        navServicePromptMessage: t('answer.unsavedChangesAlert.message'),
        confirmButton: {},
        denyButton: {},
        dismissButton: {},
        // Allow free navigation between Answer and Spreadsheet (sibling routes
        // under the same saved-answer/:answerId). They share session state so no
        // data is lost in transit — only block transitions that leave the context.
        shouldBlockRouteTransition: ({ currentLocation, nextLocation }) => {
            const strip = (p: string) => p.replace(/\/spreadsheet\/?$/, '');
            const base = strip(currentLocation.pathname);
            const nextBase = strip(nextLocation.pathname);
            return base !== nextBase;
        },
    };

    const getAnswer = () => {
        clearQueue();
        loadAnswer()
            .then(response => {
                const {
                    data: { Answer__load: answerSessionData },
                } = response;
                const props = getCommonAnswerPageProperties(answerSessionData);
                return setAnswerPageProperties(props);
            })
            .catch(() => {
                metricsService.collectEvent(
                    new UserAction(userActionTypeEnum.LOAD_ANSWER_FAILURE),
                    {
                        answerGuid: answerId,
                        answerContext: AnswerContextType.SavedAnswer,
                    },
                );
                logger.error('Error in loading answer');
            });
    };

    useEffect(() => {
        getAnswer();
    }, [answerId, runtimeParams]);

    // Reopen the answer in the view it was last saved from: when the loaded
    // answer carries the saved-from-spreadsheet clientState marker (and the
    // spreadsheet feature is enabled — shouldReopenInSpreadsheet gates on it),
    // replace the base saved-answer URL with the /spreadsheet sibling.
    // Evaluated once per answerId, on the first load only, so in-session
    // navigation between the Answer and Spreadsheet siblings (and reloads
    // triggered by runtime-param changes) is never overridden. Answers without
    // the marker — including every answer saved before it existed — take no
    // redirect and open exactly as they do today. An explicit /spreadsheet URL
    // is likewise left untouched.
    const location = useLocation();
    const navigate = useNavigate();
    const client = useApolloClient();
    const spreadsheetRedirectHandledFor = useRef<string | null>(null);
    // Redirect decision recorded as STATE (not just the ref) so the marker
    // clear below re-evaluates only in a render where the navigation issued
    // here is already reflected in location — never in the same flush, where
    // location.pathname is still the pre-redirect Answer URL.
    const [
        spreadsheetRedirectDecidedFor,
        setSpreadsheetRedirectDecidedFor,
    ] = useState<string | null>(null);
    useEffect(() => {
        // data.id must match the route's answerId: the load mutation retains
        // the PREVIOUS answer's result while the next load is in flight, so on
        // an A -> B navigation this effect would otherwise evaluate B's
        // redirect against A's clientState.
        if (
            !data ||
            data.id !== answerId ||
            spreadsheetRedirectHandledFor.current === answerId
        ) {
            return;
        }
        spreadsheetRedirectHandledFor.current = answerId;
        const isOnSpreadsheet = /\/spreadsheet\/?$/.test(location.pathname);
        if (!isOnSpreadsheet && shouldReopenInSpreadsheet(data.clientState)) {
            navigate(
                {
                    pathname: `${location.pathname.replace(
                        /\/+$/,
                        '',
                    )}/spreadsheet`,
                    search: location.search,
                },
                { replace: true },
            );
        }
        setSpreadsheetRedirectDecidedFor(answerId);
    }, [data, answerId, location, navigate]);

    // A save from the Answer view must persist WITHOUT the saved-from-
    // spreadsheet marker, so the answer reopens in the view it was last saved
    // from. Instead of hooking the shared save flow, drop the marker from the
    // SESSION whenever the Answer view becomes active for a marked answer:
    // session clientState is only persisted by a subsequent save, so if the
    // user saves here the cleared state is written through, and if they never
    // save the stored marker is untouched. The live cached clientState is
    // preferred over the load-time snapshot — the spreadsheet save slice
    // mirrors its marker stamp into the cache and the clear writes back the
    // cleared blob, so sibling switches re-clear only when the marker was
    // actually re-stamped, and answers without the marker cost no request.
    useEffect(() => {
        if (
            !data ||
            data.id !== answerId ||
            !answerSessionId ||
            spreadsheetRedirectDecidedFor !== answerId ||
            /\/spreadsheet\/?$/.test(location.pathname)
        ) {
            return;
        }
        const cached = readCachedAnswerClientState(client, answerId);
        const clientState = cached === undefined ? data.clientState : cached;
        if (!wasSavedFromSpreadsheet(clientState)) {
            return;
        }
        clearSavedFromSpreadsheetFlag(client, answerSessionId, clientState);
    }, [
        data,
        answerId,
        answerSessionId,
        spreadsheetRedirectDecidedFor,
        location.pathname,
        client,
    ]);

    useUpdateEffect(() => {
        if (data && answerPageProperties) {
            metricsService.collectEvent(
                new UserAction(userActionTypeEnum.LOAD_ANSWER),
                {
                    chartType: answerPageProperties.vizChartType,
                    kpiChartType: answerPageProperties.kpiChartType,
                    objectId: answerPageProperties.objectId,
                    accessType: getAccessTypeForObject(data.permission),
                },
            );
        }
    }, [data, answerPageProperties]);

    return (
        <SavedAnswerLayoutContext.Provider
            value={{
                answerId,
                answerSessionId,
                data,
                loading,
                error,
                navModalProps,
                cachedSageQueryState,
                setCachedSageQueryState,
                hasVisitedSpreadsheet,
                markSpreadsheetVisited,
            }}
        >
            {children}
        </SavedAnswerLayoutContext.Provider>
    );
};

export const SavedAnswerLayoutProvider: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    // In embedded app contexts (full-app embed), skip the layout provider so
    // SavedAnswerPage falls back to standalone mode. Spreadsheet and the shared
    // data-loading logic are not supported in embedded contexts.
    if (isEmbeddedApp()) {
        return <>{children}</>;
    }
    return (
        <SavedAnswerLayoutProviderInner>
            {children}
        </SavedAnswerLayoutProviderInner>
    );
};

export const useSavedAnswerLayoutContext = (): SavedAnswerLayoutContextValue => {
    const ctx = useContext(SavedAnswerLayoutContext);
    if (ctx === null) {
        throw new Error(
            'useSavedAnswerLayoutContext must be used within SavedAnswerLayoutProvider',
        );
    }
    return ctx;
};

/**
 * Returns the layout context when available, or null when SavedAnswerPage is
 * rendered standalone (without SavedAnswerLayoutProvider as an ancestor).
 *
 * Backward-compatibility shim — callers that do not yet wrap SavedAnswerPage
 * in SavedAnswerLayoutProvider will get null here and fall back to their own
 * loading logic.
 *
 * TODO: once every callsite is migrated to use SavedAnswerLayoutProvider,
 * remove this hook and replace usages with useSavedAnswerLayoutContext.
 */
export const useOptionalSavedAnswerLayoutContext = (): SavedAnswerLayoutContextValue | null =>
    useContext(SavedAnswerLayoutContext);
